// Themes studio (/themes): my saved themes with a quick switcher, the gallery of everyone's in the
// suite, and the editor with a live preview. A theme profile is a recipe; wearing one copies it into
// this person's own look (lib/themes/actions.ts), so nothing here changes anyone else's.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import { db } from '@/runtime'
import { useAppliedStudio, useRecord, useThemeProfiles, useResolvedAppearance } from '@/db/hooks'
import { useAccess } from '@/state/access'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import type { ThemeProfileResource } from '@/kinds/themeprofile/apply'
import { THEME_SPEC_V, type StudioMode, type ThemeSpec } from '@/kinds/themeprofile/schema'
import { canDraw, studioModeOf } from '@/lib/themeStudio'
import { galleryOrder, mayEdit, swatchOf } from '@/lib/themes/profiles'
import { STARTERS, type Starter } from '@/lib/themes/starters'
import {
  applyProfile,
  applyTheme,
  clearApplied,
  createProfile,
  deleteProfile,
  duplicateProfile,
  myAuthorKey,
  saveProfile,
} from '@/lib/themes/actions'
import { AppTabs } from '@/components/AppTabs'
import { ThemePreview } from '@/components/themes/ThemePreview'
import { StudioEditor } from '@/components/themes/StudioEditor'
import { Button, Empty, PageTitle, Sheet, cx } from '@/components/ui'
import { Gone } from './NotFound'

/** `/themes` is a prefix of the gallery's path, so which tab is on is said outright */
export const themesTabs = (on: 'mine' | 'gallery') => [
  { to: '/themes', label: 'Mine', on: on === 'mine' },
  { to: '/themes/gallery', label: 'Gallery', on: on === 'gallery' },
]

const useMe = () => {
  const name = useAccess((s) => s.name)
  const member = useAccess((s) => s.member)
  return { key: myAuthorKey(), name: member ? (name ?? 'Me') : 'Alfie' }
}

const Swatch = ({ theme }: { theme: ThemeSpec }) => {
  const s = swatchOf(theme)
  const dot = (c: string | undefined, round = false) => (
    <span
      className={cx('h-4 w-4 border border-line', round ? 'rounded-full' : 'rounded')}
      style={{ background: c ?? 'var(--color-raised)' }}
    />
  )
  return (
    <span className="flex gap-1 shrink-0" aria-hidden data-testid="swatch">
      {dot(s.background)}
      {dot(s.raised)}
      {dot(s.accent, true)}
      {dot(s.highlight ?? s.text, true)}
    </span>
  )
}

const ProfileRow = ({
  r,
  worn,
  children,
  onOpen,
}: {
  r: ThemeProfileResource
  worn: boolean
  children?: React.ReactNode
  onOpen: () => void
}) => (
  <div
    className="flex items-center gap-3 px-3 py-2.5 border-b border-line last:border-0"
    data-testid={`theme-profile-${r.id}`}
  >
    <button type="button" onClick={onOpen} className="flex-1 flex items-center gap-3 text-left min-w-0">
      <span className="text-2xl w-8 text-center">{r.spec.emoji}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate">{r.spec.name}</span>
        <span className="block text-xs text-muted truncate">
          {worn ? 'Wearing' : r.spec.author_name ? `by ${r.spec.author_name}` : 'Saved'}
          {r.spec.visibility === 'private' ? ' · only you' : ''}
        </span>
      </span>
      {canDraw(r.spec.theme) && <Swatch theme={r.spec.theme} />}
    </button>
    {children}
  </div>
)

/** Tap a theme to preview it live, then wear it, copy it, or (the author) edit and delete it. */
const PreviewSheet = ({
  r,
  onClose,
  me,
}: {
  r: ThemeProfileResource | undefined
  onClose: () => void
  me: { key: string; name: string }
}) => {
  const navigate = useNavigate()
  const resolved = useResolvedAppearance()
  const [mode, setMode] = useState<StudioMode>(studioModeOf(resolved))
  const ws = useScope((s) => s.ws)
  if (!r) return <Sheet open={false} onClose={onClose}>{null}</Sheet>
  const own = mayEdit(r, me.key)
  const drawable = canDraw(r.spec.theme)
  return (
    <Sheet
      open
      onClose={onClose}
      title={`${r.spec.emoji} ${r.spec.name}`}
      footer={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            disabled={!drawable}
            onClick={() => void applyProfile(r).then(onClose)}
            data-testid="theme-apply"
          >
            Wear this
          </Button>
          {!own && (
            <Button
              onClick={() =>
                void duplicateProfile(r, { authorName: me.name }, ws === ALL ? undefined : ws).then(onClose)
              }
              data-testid="theme-copy"
            >
              <Copy size={14} /> Copy to mine
            </Button>
          )}
          {own && (
            <>
              <Button onClick={() => void navigate({ to: '/themes/$id', params: { id: r.id } })}>
                <Pencil size={14} /> Edit
              </Button>
              <Button variant="danger" onClick={() => void deleteProfile(r.id).then(onClose)} data-testid="theme-delete">
                <Trash2 size={14} /> Delete
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          {r.spec.author_name ? `By ${r.spec.author_name}. ` : ''}Wearing it changes only your own look.
        </p>
        {drawable ? (
          <>
            <div className="flex gap-1.5">
              {(['light', 'dark', 'black'] as const).map((m) => (
                <Button key={m} variant={mode === m ? 'chipOn' : 'chip'} onClick={() => setMode(m)}>
                  {m[0]!.toUpperCase() + m.slice(1)}
                </Button>
              ))}
            </div>
            <ThemePreview spec={r.spec.theme} mode={mode} />
          </>
        ) : (
          <p className="text-sm text-muted">Made with a newer version of the app, so it can’t be shown here yet.</p>
        )}
      </div>
    </Sheet>
  )
}

export const ThemesView = () => {
  const all = useThemeProfiles()
  const me = useMe()
  const applied = useAppliedStudio()
  const ws = useScope((s) => s.ws)
  const [open, setOpen] = useState<string | undefined>()
  const mine = all.filter((r) => mayEdit(r, me.key))
  const sel = all.find((r) => r.id === open)
  return (
    <div data-testid="themes-view">
      <PageTitle
        right={
          <Link to="/themes/new" search={{}} className="text-sm text-accent-text inline-flex items-center gap-1" data-testid="theme-new">
            <Plus size={14} /> New
          </Link>
        }
      >
        Themes
      </PageTitle>
      <AppTabs tabs={themesTabs('mine')} label="Themes" />
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-none" role="radiogroup" aria-label="Quick switch">
        <button
          type="button"
          role="radio"
          aria-checked={!applied}
          onClick={() => void clearApplied()}
          className={cx(
            'shrink-0 rounded-full border px-3 min-h-11 text-sm',
            !applied ? 'border-accent bg-accent-soft' : 'border-line text-muted',
          )}
        >
          Default
        </button>
        {mine.map((r) => (
          <button
            key={r.id}
            type="button"
            role="radio"
            aria-checked={applied?.profile === r.id}
            onClick={() => void applyProfile(r)}
            className={cx(
              'shrink-0 rounded-full border px-3 min-h-11 text-sm',
              applied?.profile === r.id ? 'border-accent bg-accent-soft' : 'border-line text-muted',
            )}
            data-testid={`theme-chip-${r.id}`}
          >
            {r.spec.emoji} {r.spec.name}
          </button>
        ))}
      </div>
      <h2 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">My themes</h2>
      {mine.length === 0 ? (
        <Empty>Nothing saved yet. Start from one below, or make your own.</Empty>
      ) : (
        <div className="group-list mb-5">
          {mine.map((r) => (
            <ProfileRow key={r.id} r={r} worn={applied?.profile === r.id} onOpen={() => setOpen(r.id)}>
              <Link to="/themes/$id" params={{ id: r.id }} aria-label={`Edit ${r.spec.name}`} className="p-3 text-muted">
                <Pencil size={16} />
              </Link>
              <button
                type="button"
                aria-label={`Duplicate ${r.spec.name}`}
                className="p-3 text-muted"
                onClick={() => void duplicateProfile(r, { authorName: me.name }, ws === ALL ? undefined : ws)}
              >
                <Copy size={16} />
              </button>
            </ProfileRow>
          ))}
        </div>
      )}
      <h2 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">Start from</h2>
      <div className="group-list">
        {STARTERS.map((s) => (
          <StarterRow key={s.id} s={s} />
        ))}
      </div>
      <PreviewSheet r={sel} onClose={() => setOpen(undefined)} me={me} />
    </div>
  )
}

const StarterRow = ({ s }: { s: Starter }) => {
  const navigate = useNavigate()
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-line last:border-0" data-testid={`starter-${s.id}`}>
      <span className="text-2xl w-8 text-center">{s.emoji}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium">{s.name}</span>
        <span className="block text-xs text-muted">{s.blurb}</span>
      </span>
      <Swatch theme={s.theme} />
      <Button
        variant="chip"
        onClick={() => void navigate({ to: '/themes/new', search: { from: s.id } })}
        data-testid={`starter-use-${s.id}`}
      >
        Use
      </Button>
    </div>
  )
}

export const ThemeGalleryView = () => {
  const all = useThemeProfiles()
  const me = useMe()
  const [open, setOpen] = useState<string | undefined>()
  const list = useMemo(() => galleryOrder(all.filter((r) => r.spec.visibility === 'workspace' || mayEdit(r, me.key)), me.key), [all, me.key])
  const sel = all.find((r) => r.id === open)
  return (
    <div data-testid="theme-gallery">
      <PageTitle>Themes</PageTitle>
      <AppTabs tabs={themesTabs('gallery')} label="Themes" />
      {list.length === 0 ? (
        <Empty>Save a theme and it shows up here for everyone in the suite.</Empty>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {list.map((r) => {
            const s = swatchOf(r.spec.theme)
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setOpen(r.id)}
                className="card p-3 text-left flex flex-col gap-2"
                data-testid={`gallery-${r.id}`}
              >
                <span
                  className="h-14 rounded-[var(--radius-control)] border border-line flex items-end gap-1 p-1.5"
                  style={{ background: s.background ?? 'var(--color-ink)' }}
                  aria-hidden
                >
                  <span className="h-4 flex-1 rounded" style={{ background: s.raised ?? 'var(--color-raised)' }} />
                  <span className="h-4 w-4 rounded-full" style={{ background: s.accent ?? 'var(--color-accent)' }} />
                  <span className="h-4 w-4 rounded-full" style={{ background: s.highlight ?? s.text ?? 'var(--color-fg)' }} />
                </span>
                <span className="font-medium truncate">
                  {r.spec.emoji} {r.spec.name}
                </span>
                <span className="text-xs text-muted truncate" data-testid="gallery-author">
                  {mayEdit(r, me.key) ? 'You' : (r.spec.author_name || 'Someone')}
                </span>
              </button>
            )
          })}
        </div>
      )}
      <PreviewSheet r={sel} onClose={() => setOpen(undefined)} me={me} />
    </div>
  )
}

const DRAFT = (id: string) => `theme-draft:${id}`

export const ThemeEditView = () => {
  const { id } = useParams({ strict: false }) as { id?: string }
  const from = new URLSearchParams(window.location.search).get('from')
  const rec = useRecord<ThemeProfileResource['spec']>(id)
  const me = useMe()
  if (id && rec === undefined) return null
  if (id && (!rec || rec.deleted_at)) return <Gone kind="page" deleted={!!rec?.deleted_at} />
  const profile = rec as ThemeProfileResource | null | undefined
  if (profile && !mayEdit(profile, me.key)) return <Gone kind="page" />
  const starter = STARTERS.find((s) => s.id === from)
  return <Editor key={id ?? 'new'} profile={profile ?? undefined} starter={starter} me={me} />
}

const Editor = ({
  profile,
  starter,
  me,
}: {
  profile?: ThemeProfileResource
  starter?: Starter
  me: { key: string; name: string }
}) => {
  const navigate = useNavigate()
  const ws = useScope((s) => s.ws)
  const resolved = useResolvedAppearance()
  const key = DRAFT(profile?.id ?? 'new')
  const initial: ThemeSpec = profile?.spec.theme ?? starter?.theme ?? { v: THEME_SPEC_V }
  const [spec, setSpec] = useState<ThemeSpec>(initial)
  const [name, setName] = useState(profile?.spec.name ?? starter?.name ?? '')
  const [emoji, setEmoji] = useState(profile?.spec.emoji ?? starter?.emoji ?? '🎨')
  const [shared, setShared] = useState((profile?.spec.visibility ?? 'workspace') === 'workspace')
  const [mode, setMode] = useState<StudioMode>(studioModeOf(resolved))
  const [error, setError] = useState('')
  const loaded = useRef(false)
  // a draft survives closing the app: it is kept on the device as it changes, and cleared on save
  useEffect(() => {
    void db.getMeta<{ spec: ThemeSpec; name: string; emoji: string; shared: boolean }>(key).then((d) => {
      if (d && !loaded.current) {
        setSpec(d.spec)
        setName(d.name)
        setEmoji(d.emoji)
        setShared(d.shared)
      }
      loaded.current = true
    })
  }, [key])
  useEffect(() => {
    if (loaded.current) void db.setMeta(key, { spec, name, emoji, shared })
  }, [key, spec, name, emoji, shared])
  const dirty =
    !profile ||
    JSON.stringify(spec) !== JSON.stringify(profile.spec.theme) ||
    name !== profile.spec.name ||
    emoji !== profile.spec.emoji ||
    shared !== (profile.spec.visibility === 'workspace')
  const save = async (andWear: boolean) => {
    if (!name.trim()) return setError('Give it a name')
    setError('')
    try {
      const visibility = shared ? 'workspace' : 'private'
      if (profile) {
        await saveProfile(profile, { name, emoji, visibility, theme: spec })
        if (andWear) await applyTheme(spec, profile.id)
      } else {
        const r = await createProfile(
          { name, emoji, theme: spec, visibility, authorName: me.name },
          ws === ALL ? undefined : ws,
        )
        if (andWear) await applyTheme(spec, (r as { id?: string } | undefined)?.id)
      }
      await db.setMeta(key, undefined)
      void navigate({ to: '/themes' })
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <div data-testid="theme-editor-view">
      <PageTitle>{profile ? 'Edit theme' : 'New theme'}</PageTitle>
      <div className="flex gap-2 mb-3">
        <input
          value={emoji}
          onChange={(e) => setEmoji([...e.target.value].slice(-2).join('') || '🎨')}
          className="w-14 text-center text-xl"
          aria-label="Emoji"
          data-testid="theme-emoji"
        />
        <input
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="flex-1"
          aria-label="Name"
          data-testid="theme-name"
        />
      </div>
      <div className="sticky top-0 z-10 pb-3 -mx-1 px-1" style={{ background: 'var(--color-ink)' }}>
        <ThemePreview spec={spec} mode={mode} compact />
      </div>
      <StudioEditor spec={spec} mode={mode} onMode={setMode} onChange={setSpec} />
      <label className="flex items-center gap-3 min-h-11 mt-2">
        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="theme-shared" />
        <span className="text-sm">
          Share in the gallery
          <span className="block text-xs text-faint">
            {shared ? 'Everyone in the suite can see it and copy it' : 'Only you (and Alfie, who runs the server) can see it'}
          </span>
        </span>
      </label>
      {error && (
        <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }} role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2 mt-4 pb-6">
        <Button variant="primary" big disabled={!dirty} onClick={() => void save(false)} data-testid="theme-save">
          Save
        </Button>
        <Button big onClick={() => void save(true)} data-testid="theme-save-wear">
          Save and wear
        </Button>
      </div>
    </div>
  )
}
